import React, { useMemo, useRef, useState } from "react";
import CardStatusType from "enums/CardStatusType";
import type {
  CardElementOptions,
  CardIssuerFeatures,
} from "utils/cards/cardViewer";
import ExternalCardDesign from "assets/images/ExternalCardDesign.svg";
import { useAuth } from "@frontegg/react";
import CardType from "enums/CardType";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { usePaymentCardTextStyle, toast } from "@outpave/ds";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";
import { fullName } from "utils/fullName";

interface PaymentCardFaceProps {
  cardId?: string | undefined;
  cardData?: any;
  handleViewCard?: (id: string, style: CardElementOptions) => Promise<boolean>;
  /** The features of the issuer drawing this card's details, once a reveal has chosen it. */
  issuerFeatures?: CardIssuerFeatures;
  cardLoading?: boolean;
}

const PaymentCardFace: React.FC<PaymentCardFaceProps> = (props) => {
  const { user, isAuthenticated } = useAuth();
  let { cardId, cardData, handleViewCard, issuerFeatures, cardLoading } = props;
  const { t } = useTranslation();

  // The issuer's fields are cross-origin iframes: they take a style object and
  // cannot resolve a var(). This reads the real face and re-reads it when the
  // theme flips, which is what the old getComputedStyle("--cardTextColor") was
  // approximating against a variable nothing writes any more (R4).
  const cardRef = useRef<HTMLDivElement>(null);
  const cardTextStyle = usePaymentCardTextStyle(cardRef);

  const [isHovered, setIsHovered] = useState(false);
  const [revealed, setRevealed] = useState(false);

  if (!cardId) cardId = "";
  if (!cardData)
    cardData = {
      card: {
        id: "",
        cardNumber: "****",
        type: "VIRTUAL",
        status: CardStatusType.PENDING,
        user: {
          firstName: "",
          lastName: "",
        },
        pendingRequest: {
          requestData: {
            card_expiration_date: "",
          },
        },
      },
    };

  const handleClick = async () => {
    if (cardData.card.status !== CardStatusType.ACTIVE) {
      toast.error("Only active cards can be viewed.");
      return;
    }
    if (card?.type === CardType.PHYSICAL) {
      toast.error("Physical card details are not available online.");
      return;
    }
    if (card?.type === CardType.EXTERNAL) {
      toast.error("External card details are not available here.");
      return;
    }
    if (cardData.card.user.userId !== user?.id) {
      toast.error("Only owners can view their card details.");
      return;
    }

    if (cardId && cardId !== "" && handleViewCard) {
      setRevealed(true);
      const shown = await handleViewCard(cardId, {
        style: {
          base: { ...cardTextStyle, fontSmoothing: "antialiased" },
        },
      });
      // A failed reveal puts the masked card back, so it can be tried again.
      if (!shown) setRevealed(false);
    }
  };

  const card = useMemo(() => {
    return cardData?.card;
  }, [cardData]);

  const accessCard = useMemo(() => {
    return card?.user?.userId === user?.id;
  }, [card?.user?.userId, user?.id]);

  if (!isAuthenticated) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CompanyPaymentCard
        ref={cardRef}
        backgroundImage={
          card?.type === CardType.EXTERNAL ? ExternalCardDesign : undefined
        }
        brandMark={
          card?.type === CardType.EXTERNAL ? (
            <RenderMerchantDisplay
              merchant={card?.account?.name}
              url={card?.account?.url}
              logoType="logo"
              fallback="transparent"
              theme="light"
              height={40}
              width={164}
              withTitle={false}
              withLink={false}
            />
          ) : undefined
        }
        holder={fullName(card?.user)}
        number={
          <div id={`card-number-${cardId}`}>
            {!revealed ? `**** **** **** ${card?.cardNumber}` : ""}
          </div>
        }
        numberOverlay={
          issuerFeatures?.copyButton ? (
            <div id={`card-number-copy-${cardId}`} />
          ) : undefined
        }
        cvv={
          card?.type !== CardType.EXTERNAL ? (
            <div id={`card-cvc-${cardId}`}>{!revealed ? "***" : ""}</div>
          ) : undefined
        }
        expiry={<div id={`card-exp-${cardId}`}>{!revealed ? "**/**" : ""}</div>}
        onClick={!revealed ? handleClick : undefined}
        actionLabel={t("Click to reveal")}
        overlay={
          isHovered && accessCard && !revealed ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--color-bg-overlay)",
                borderRadius: "inherit",
              }}
            >
              {t("Click to reveal")}
            </Box>
          ) : undefined
        }
      />
      <LoadingWithoutModal open={cardLoading ?? false} />
    </Box>
  );
};

export default PaymentCardFace;
